import { DOMAIN } from '@pairy/sheard/constants';

// QR 코드 보기에서 생성되는 QR URL에 기본으로 추가하는 UTM
export const QR_UTM = {
  utm_source: 'qr_code',
  utm_medium: 'offline',
  utm_campaign: 'qr_code',
} as const;

/**
 * 대량발송용 공유 링크 URL을 QR 전용 URL로 변환한다.
 * - 기존 URL에 있던 모든 utm_* 파라미터는 제거하고 QR 기본 UTM으로 대체한다.
 * - utm 외 다른 쿼리 파라미터(foo=bar 등)는 그대로 유지한다.
 * - utm_content는 추가하지 않는다.
 */
export const createQrShareUrl = (shareUrl: string) => {
  const url = new URL(shareUrl);

  Array.from(url.searchParams.keys())
    .filter((key) => key.startsWith('utm_'))
    .forEach((key) => url.searchParams.delete(key));

  Object.entries(QR_UTM).forEach(([key, value]) => {
    url.searchParams.set(key, value);
  });

  return url.toString();
};

/**
 * 공유 링크가 열릴 뷰어 origin. 기존 get*ShareLink 들과 같은 호스트 분기를 따른다.
 */
export const getViewerOrigin = ({ domain }: { domain?: string }) => {
  const { hostname } = window.location;

  // CASE1. 유저가 세팅한 커스텀 도메인이 있는 경우
  if (domain && domain !== null) {
    return `https://${domain}`;
  }

  // CASE2. alpha.salesclue.io인 경우
  if (hostname.includes(DOMAIN.CLIENT.release)) {
    return `https://${DOMAIN.VIEWER.release}`;
  }

  // CASE3. app.salesclue.io인 경우
  if (hostname.includes(DOMAIN.CLIENT.main)) {
    return `https://${DOMAIN.VIEWER.main}`;
  }

  // CASE4. 로컬 개발 중일 때
  if (hostname.includes(DOMAIN.CLIENT.local)) {
    return 'http://localhost:3002';
  }

  // CASE5. dev환경일 때
  return `https://${DOMAIN.VIEWER.dev}`;
};

/**
 * 단축 공유 링크. 기존 /share/... URL과 동시에 유효하다.
 */
export const getShortShareLink = ({
  token,
  domain,
}: {
  token: string;
  domain?: string;
}) => `${getViewerOrigin({ domain })}/s/${token}`;

/**
 * ESP 머지태그(`?email=$%email%$` 등)처럼 `?`로 시작하는 접미사를 URL에 붙인다.
 * 머지태그는 인코딩되면 안 되므로 URL 파서를 쓰지 않고 문자열로 결합하되,
 * 이미 쿼리가 있는 URL이면 `?`를 `&`로 바꿔 깨지지 않게 한다.
 */
export const appendQuerySuffix = (url: string, suffix: string) => {
  if (!suffix) {
    return url;
  }

  if (url.includes('?') && suffix.startsWith('?')) {
    return `${url}&${suffix.slice(1)}`;
  }

  return `${url}${suffix}`;
};

interface GetShareLinkProps {
  hashId: string;
  isBulk: boolean;
  domain?: string;
}

export const getFileShareLink = ({
  hashId,
  isBulk,
  domain,
}: GetShareLinkProps) => {
  const { hostname } = window.location;

  //CASE1. 유저가 세팅한 커스텀 도메인이 있는 경우
  if (domain && domain !== null) {
    const shareLink = isBulk
      ? `https://${domain}/share/bulk/file/${hashId}`
      : `https://${domain}/share/file/${hashId}`;

    return shareLink;
  }

  // CASE2. alpha.salesclue.io인 경우
  if (hostname.includes(DOMAIN.CLIENT.release)) {
    const shareLink = isBulk
      ? `https://${DOMAIN.VIEWER.release}/share/bulk/file/${hashId}`
      : `https://${DOMAIN.VIEWER.release}/share/file/${hashId}`;

    return shareLink;
  }

  // CASE3. app.salesclue.io인 경우
  if (hostname.includes(DOMAIN.CLIENT.main)) {
    const shareLink = isBulk
      ? `https://${DOMAIN.VIEWER.main}/share/bulk/file/${hashId}`
      : `https://${DOMAIN.VIEWER.main}/share/file/${hashId}`;

    return shareLink;
  }

  // CASE4. 로컬 개발 중일 때
  if (hostname.includes(DOMAIN.CLIENT.local)) {
    const shareLink = isBulk
      ? `http://localhost:3002/share/bulk/file/${hashId}`
      : `http://localhost:3002/share/file/${hashId}`;

    return shareLink;
  }

  // CASE5. dev환경일 때

  const shareLink = isBulk
    ? `https://${DOMAIN.VIEWER.dev}/share/bulk/file/${hashId}`
    : `https://${DOMAIN.VIEWER.dev}/share/file/${hashId}`;

  return shareLink;
};

export const getBulkLinkShareLink = ({
  slug,
  domain,
}: {
  slug: string;
  domain?: string;
}) => {
  const { hostname } = window.location;

  if (domain && domain !== null) {
    return `https://${domain}/share/bulk/link/${slug}`;
  }

  if (hostname.includes(DOMAIN.CLIENT.release)) {
    return `https://${DOMAIN.VIEWER.release}/share/bulk/link/${slug}`;
  }

  if (hostname.includes(DOMAIN.CLIENT.main)) {
    return `https://${DOMAIN.VIEWER.main}/share/bulk/link/${slug}`;
  }

  if (hostname.includes(DOMAIN.CLIENT.local)) {
    return `http://localhost:3002/share/bulk/link/${slug}`;
  }

  return `https://${DOMAIN.VIEWER.dev}/share/bulk/link/${slug}`;
};

export const getLinkShareLink = ({
  hashId,
  domain,
}: {
  hashId: string;
  domain?: string;
}) => {
  const { hostname } = window.location;

  if (domain && domain !== null) {
    const shareLink = `https://${domain}/share/link/${hashId}`;

    return shareLink;
  }

  if (hostname.includes(DOMAIN.CLIENT.main)) {
    const shareLink = `https://${DOMAIN.VIEWER.main}/share/link/${hashId}`;

    return shareLink;
  }

  if (hostname.includes(DOMAIN.CLIENT.local)) {
    const shareLink = `http://localhost:3002/share/link/${hashId}`;

    return shareLink;
  }

  const shareLink = `https://${DOMAIN.VIEWER.dev}/share/link/${hashId}`;

  return shareLink;
};
